import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, InputNumber, Popconfirm, Popover, Select, Table, Tag, message } from 'antd';
import { DeleteOutlined, PlusOutlined, SaveOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  RangeMetric,
  ScreenCriterion,
  ScreenMetric,
  ScreenPreset,
  ScreenResult,
  ScreenRow,
  SCREEN_EXCHANGES,
  SCREEN_INDEXES,
  smartBetaApi,
} from '../../../services/smartBetaApi';
import { formatNumber, numberInputProps } from '../../../utils/numberFormat';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { basketOf, MAX_TICKERS, SCREEN_OPTIONS, screenProblem, SmartBetaForm } from '../smartBetaForm';
import { Muted, Row } from '../styles';

interface Props {
  form: SmartBetaForm;
  onChange: (patch: Partial<SmartBetaForm>) => void;
  disabled?: boolean;
}

// An emptied range box is an open end, not 0.
const rangeInputProps = () => {
  const base = numberInputProps();
  return { ...base, parser: (v: string | undefined) => (v?.trim() ? base.parser(v) : ('' as unknown as number)) };
};

const PREVIEW_POLL_MS = 1_000;
const PREVIEW_TIMEOUT_MS = 3 * 60_000;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const errorText = (e: any, fallback: string) => e?.response?.data?.message ?? e?.message ?? fallback;

const blank = (metric: ScreenMetric): ScreenCriterion =>
  metric === 'EXCHANGE'
    ? { metric, values: ['HSX'] }
    : metric === 'INDEX'
      ? { metric, values: ['VN30'] }
      : { metric, min: null, max: null };

/** Screens whose picks join the basket as auto tickers, the user's saved presets, and a preview. */
const ScreenEditor: React.FC<Props> = ({ form, onChange, disabled }) => {
  const isMobile = useIsMobile();
  const [presets, setPresets] = useState<ScreenPreset[]>([]);
  const [presetName, setPresetName] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);
  const [preview, setPreview] = useState<ScreenResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const problem = screenProblem(form);
  const used = new Set(form.criteria.map((c) => c.metric));

  const loadPresets = useCallback(() => {
    smartBetaApi
      .presets()
      .then(setPresets)
      .catch(() => setPresets([]));
  }, []);
  useEffect(loadPresets, [loadPresets]);

  // A preview runs as a queued job and belongs to the screens it ran on: an edit or a newer
  // preview makes an older one moot.
  const previewRun = useRef(0);
  const criteriaKey = JSON.stringify([form.criteria, form.screenLimit, form.whitelist, form.blacklist]);
  useEffect(() => {
    previewRun.current += 1;
    setPreview(null);
    setPreviewError(null);
    setPreviewing(false);
  }, [criteriaKey]);

  const setCriterion = (i: number, patch: Partial<ScreenCriterion>) =>
    onChange({ criteria: form.criteria.map((c, j) => (j === i ? { ...c, ...patch } : c)) });

  const runPreview = async () => {
    const run = ++previewRun.current;
    setPreviewing(true);
    setPreviewError(null);
    try {
      let job = await smartBetaApi.screen(basketOf(form));
      const deadline = Date.now() + PREVIEW_TIMEOUT_MS;
      while (job.status === 'PENDING' || job.status === 'RUNNING') {
        if (Date.now() > deadline) throw new Error('The screen is taking long — try the preview again later');
        await sleep(PREVIEW_POLL_MS);
        if (run !== previewRun.current) return;
        job = await smartBetaApi.screenJob(job.id);
      }
      if (run !== previewRun.current) return;
      if (job.status === 'FAILED') setPreviewError(job.error ?? 'Screen failed');
      else setPreview(job.result ?? null);
    } catch (e: any) {
      if (run === previewRun.current) setPreviewError(errorText(e, 'Screen failed'));
    } finally {
      if (run === previewRun.current) setPreviewing(false);
    }
  };

  const savePreset = async () => {
    try {
      await smartBetaApi.savePreset(presetName.trim(), form.criteria, form.screenLimit);
      message.success(`Saved "${presetName.trim()}"`);
      setSaveOpen(false);
      setPresetName('');
      loadPresets();
    } catch (e: any) {
      message.error(errorText(e, 'Could not save the preset'));
    }
  };

  const applyPreset = (id: string) => {
    const p = presets.find((x) => x.id === id);
    if (p) onChange({ criteria: p.criteria.map((c) => ({ ...c })), screenLimit: p.screenLimit });
  };

  const deletePreset = async (p: ScreenPreset) => {
    try {
      await smartBetaApi.deletePreset(p.id);
      loadPresets();
    } catch (e: any) {
      message.error(errorText(e, 'Could not delete the preset'));
    }
  };

  const addOptions = SCREEN_OPTIONS.filter((o) => !used.has(o.metric)).map((o) => ({
    value: o.metric,
    search: `${o.label} ${o.description}`.toLowerCase(),
    label: (
      <div style={{ whiteSpace: 'normal', padding: '2px 0' }}>
        <div>
          {o.label}
          {o.unit && <Muted> ({o.unit})</Muted>}
        </div>
        <Muted style={{ fontSize: 12 }}>{o.description}</Muted>
      </div>
    ),
  }));

  // The preview shows liquidity always, then the figure of each range screen in use.
  const shown = form.criteria
    .map((c) => SCREEN_OPTIONS.find((o) => o.metric === c.metric)!)
    .filter((o) => o.kind === 'range' && o.metric !== 'AVG_VALUE_20D')
    .slice(0, isMobile ? 1 : undefined);
  const previewColumns: ColumnsType<ScreenRow> = [
    {
      title: 'Ticker',
      dataIndex: 'symbol',
      render: (s: string, r) => (
        <span>
          {s} <Tag style={{ marginLeft: 4 }}>{r.exchange}</Tag>
        </span>
      ),
    },
    { title: 'Price (k)', dataIndex: 'price', align: 'right', render: (v: number) => formatNumber(v) },
    {
      title: 'Value/day (bn)',
      align: 'right',
      render: (_, r) => formatNumber(r.metrics.AVG_VALUE_20D),
    },
    ...shown.map((o) => ({
      title: `${o.label} (${o.unit})`,
      key: o.metric,
      align: 'right' as const,
      render: (_: unknown, r: ScreenRow) => formatNumber(r.metrics[o.metric as RangeMetric]),
    })),
  ];

  return (
    <div>
      {presets.length > 0 && (
        <Row>
          <Select
            placeholder="Load a saved screen"
            value={null}
            onChange={applyPreset}
            disabled={disabled}
            style={{ width: 240, maxWidth: '100%' }}
            aria-label="Saved screens"
            options={presets.map((p) => ({
              value: p.id,
              label: (
                <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span>{p.name}</span>
                  <Popconfirm title={`Delete "${p.name}"?`} onConfirm={() => deletePreset(p)}>
                    <DeleteOutlined
                      aria-label={`Delete ${p.name}`}
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: '#848E9C' }}
                    />
                  </Popconfirm>
                </span>
              ),
            }))}
          />
        </Row>
      )}

      {form.criteria.map((c, i) => {
        const opt = SCREEN_OPTIONS.find((o) => o.metric === c.metric)!;
        return (
          <div
            key={c.metric}
            role="group"
            aria-label={`${opt.label} screen`}
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'minmax(0, 1fr) 32px' : '200px minmax(0, 1fr) 32px',
              gap: 8,
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <div style={{ color: '#EAECEF', gridColumn: isMobile ? '1 / 2' : undefined }} title={opt.description}>
              {opt.label}
              {opt.unit && <Muted> ({opt.unit})</Muted>}
            </div>
            {isMobile && <span />}
            {opt.kind === 'pick' ? (
              <Select
                mode="multiple"
                value={c.values}
                onChange={(values) => setCriterion(i, { values })}
                disabled={disabled}
                aria-label={`${opt.label} values`}
                options={(c.metric === 'EXCHANGE' ? SCREEN_EXCHANGES : SCREEN_INDEXES).map((v) => ({
                  value: v,
                  label: v,
                }))}
              />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <InputNumber
                  {...rangeInputProps()}
                  value={c.min ?? null}
                  onChange={(v) => setCriterion(i, { min: v ?? null })}
                  min={opt.signed ? undefined : 0}
                  placeholder="Min"
                  disabled={disabled}
                  aria-label={`${opt.label} min`}
                  style={{ width: '100%' }}
                />
                <span style={{ color: '#848E9C' }}>–</span>
                <InputNumber
                  {...rangeInputProps()}
                  value={c.max ?? null}
                  onChange={(v) => setCriterion(i, { max: v ?? null })}
                  min={opt.signed ? undefined : 0}
                  placeholder="Max"
                  disabled={disabled}
                  aria-label={`${opt.label} max`}
                  style={{ width: '100%' }}
                />
              </div>
            )}
            <Button
              type="text"
              icon={<DeleteOutlined />}
              onClick={() => onChange({ criteria: form.criteria.filter((_, j) => j !== i) })}
              disabled={disabled}
              aria-label={`Remove ${opt.label}`}
              style={{ color: '#848E9C' }}
            />
          </div>
        );
      })}

      <Row>
        <Select
          showSearch
          value={null}
          placeholder={
            <span>
              <PlusOutlined /> Add filter — search price, volume, foreign…
            </span>
          }
          onChange={(metric: ScreenMetric) => onChange({ criteria: [...form.criteria, blank(metric)] })}
          filterOption={(input, option) => !!option?.search.includes(input.trim().toLowerCase())}
          options={addOptions}
          disabled={disabled || addOptions.length === 0}
          aria-label="Add filter"
          notFoundContent="No filter matches"
          listHeight={360}
          popupMatchSelectWidth={isMobile ? true : 420}
          style={{ width: isMobile ? '100%' : 320 }}
        />
        {form.criteria.length > 0 && (
          <Popover
            open={saveOpen}
            onOpenChange={setSaveOpen}
            trigger="click"
            content={
              <div style={{ display: 'flex', gap: 8 }}>
                <Input
                  autoFocus
                  value={presetName}
                  maxLength={60}
                  placeholder="Preset name"
                  onChange={(e) => setPresetName(e.target.value)}
                  onPressEnter={() => presetName.trim() && savePreset()}
                  aria-label="Preset name"
                />
                <Button type="primary" disabled={!presetName.trim()} onClick={savePreset}>
                  Save
                </Button>
              </div>
            }
          >
            <Button type="link" size="small" icon={<SaveOutlined />} disabled={disabled || !!problem}>
              Save as preset
            </Button>
          </Popover>
        )}
      </Row>

      {form.criteria.length > 0 && (
        <>
          <Row>
            <span>Keep the</span>
            <InputNumber
              {...numberInputProps()}
              min={1}
              max={MAX_TICKERS}
              precision={0}
              value={form.screenLimit}
              onChange={(v) => onChange({ screenLimit: v || 1 })}
              disabled={disabled}
              aria-label="Screened tickers to keep"
              style={{ width: 72 }}
            />
            <span>most liquid matches, as Auto tickers</span>
            <Button
              size="small"
              icon={<SearchOutlined />}
              onClick={runPreview}
              loading={previewing}
              disabled={!!problem}
              style={{ marginLeft: 'auto' }}
            >
              {previewing ? 'Screening…' : 'Preview'}
            </Button>
          </Row>
          <Muted>
            The screens run again at every save and every rebalance, on the latest closed session. White and black list
            tickers are left out.
          </Muted>
          {previewError && <Alert type="error" showIcon style={{ marginTop: 8 }} message={previewError} />}
          {preview && (
            <div style={{ marginTop: 8 }} aria-label="Screen preview">
              <Muted>
                {preview.matched} match{preview.matched === 1 ? '' : 'es'}
                {preview.asOf ? ` as of ${preview.asOf}` : ''} — keeping {preview.picked.length}
              </Muted>
              <Table
                size="small"
                rowKey="symbol"
                pagination={false}
                scroll={{ x: true }}
                dataSource={preview.picked}
                style={{ marginTop: 6 }}
                columns={previewColumns}
              />
            </div>
          )}
        </>
      )}
      {problem && form.criteria.length > 0 && <Muted style={{ color: '#F0B90B' }}>{problem}</Muted>}
    </div>
  );
};

export default ScreenEditor;
